import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { useEffect, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import logo from "./assets/logo.png";
import useAdmin from "./hooks/useAdmin";

const logoSrc = logo.startsWith("/")
  ? logo
  : `/${logo.replace(/^\.?\//, "")}`;

function Navbar() {
  const { isAdmin } = useAdmin();
  const [username, setUsername] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const menuRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsLoggedIn(false);
        setUsername("");
        return;
      }

      setIsLoggedIn(true);

      const snapshot = await getDocs(collection(db, "profiles"));

      snapshot.forEach((docItem) => {
        const data = docItem.data();

        if (data.userId === user.uid) {
          setUsername(data.username);
        }
      });
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  if (isLoggedIn && isMobile) {
    return null;
  }

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <div style={navStyle}>
      <div style={navInnerStyle}>
        <Link to="/" style={logoLinkStyle} onClick={closeMenu}>
          <img src={logoSrc} alt="MyBuildID" style={logoImageStyle} />
        </Link>

        {isLoggedIn ? (
          <div ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={menuButtonStyle}
            >
              ☰
            </button>

            {menuOpen && (
              <div style={dropdownStyle}>
                <Link to="/" style={linkStyle} onClick={closeMenu}>
                  Home
                </Link>

                <Link to="/discover" style={linkStyle} onClick={closeMenu}>
                  Discover
                </Link>

                <Link to="/spotlight" style={linkStyle} onClick={closeMenu}>
                  Spotlight
                </Link>

                <Link to="/for-sale" style={linkStyle} onClick={closeMenu}>
                  For Sale
                </Link>

                <Link to="/saved" style={linkStyle} onClick={closeMenu}>
                  Saved
                </Link>

                <Link to="/garage" style={linkStyle} onClick={closeMenu}>
                  Garage
                </Link>

                <Link to="/account" style={linkStyle} onClick={closeMenu}>
                  Account
                </Link>
                {isAdmin && <Link to="/admin/vehicle-artwork" style={linkStyle} onClick={closeMenu}>Admin · Vehicle Artwork</Link>}

                {username && (
                  <Link
                    to={`/user/${username}`}
                    style={linkStyle}
                    onClick={closeMenu}
                  >
                    Public Profile
                  </Link>
                )}

                <Link to="/about" style={linkStyle} onClick={closeMenu}>
                  About
                </Link>

                <Link to="/contact" style={linkStyle} onClick={closeMenu}>
                  Contact
                </Link>

                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    try {
                      await signOut(auth);
                      setUsername("");
                      setIsLoggedIn(false);
                      setMenuOpen(false);
                      window.location.replace("/");
                    } catch (error) {
                      alert(error.message);
                    }
                  }}
                  style={logoutStyle}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <div ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={menuButtonStyle}
            >
              ☰
            </button>

            {menuOpen && (
              <div style={dropdownStyle}>
                <Link to="/discover" style={linkStyle} onClick={closeMenu}>
                  Discover
                </Link>

                <Link to="/about" style={linkStyle} onClick={closeMenu}>
                  About
                </Link>

                <Link to="/contact" style={linkStyle} onClick={closeMenu}>
                  Contact
                </Link>

                <Link to="/auth" style={linkStyle} onClick={closeMenu}>
                  Access or Create Your Garage
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const navStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  backgroundColor: "#111",
  borderBottom: "1px solid #242424",
  height: "calc(72px + env(safe-area-inset-top))",
  padding: "env(safe-area-inset-top) 16px 0 16px",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const navInnerStyle = {
  width: "100%",
  maxWidth: "1100px",
  height: "72px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const logoLinkStyle = {
  display: "flex",
  alignItems: "center",
  height: "72px",
  width: "180px",
  textDecoration: "none",
  overflow: "hidden",
};

const logoImageStyle = {
  height: "52px",
  width: "180px",
  objectFit: "contain",
  display: "block",
};

const menuButtonStyle = {
  backgroundColor: "transparent",
  border: "none",
  color: "white",
  fontSize: "28px",
  cursor: "pointer",
  height: "72px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0",
};

const dropdownStyle = {
  backgroundColor: "#181818",
  border: "1px solid #242424",
  borderRadius: "16px",
  display: "flex",
  flexDirection: "column",
  padding: "10px 16px",
  position: "fixed",
  top: "calc(72px + env(safe-area-inset-top))",
  right: "20px",
  width: "240px",
  minWidth: "220px",
  maxWidth: "90vw",
  boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
  zIndex: 10000,
};

const linkStyle = {
  color: "white",
  textDecoration: "none",
  padding: "14px 0",
  borderBottom: "1px solid #242424",
  fontWeight: "600",
};

const logoutStyle = {
  backgroundColor: "transparent",
  border: "none",
  color: "#e10600",
  textAlign: "left",
  padding: "14px 0",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "16px",
};

export default Navbar;
